import { relativeTime, type DeviceRecord } from '@bsync/shared';
import { For, Show, createMemo, createSignal, onCleanup, onMount } from 'solid-js';
import { ConfirmDialog } from '../components/Dialog';
import { BrowserMark, IconEdit } from '../components/Icons';
import { currentDeviceId, disconnectDeviceById, renameDeviceById, state } from '../lib/app';
import { toast } from '../lib/toasts';

const ONLINE_WINDOW = 5 * 60_000;

export default function DevicesPage() {
  const [now, setNow] = createSignal(Date.now());
  const [confirm, setConfirm] = createSignal<DeviceRecord | null>(null);
  onMount(() => {
    const t = setInterval(() => setNow(Date.now()), 30_000);
    onCleanup(() => clearInterval(t));
  });
  const browsers = createMemo(() => (state.devices() ?? []).filter((d) => d.browser !== 'web'));
  const web = createMemo(() => (state.devices() ?? []).filter((d) => d.browser === 'web'));

  return (
    <div class="page">
      <header class="page-head">
        <div class="head-row">
          <h1>Devices</h1>
        </div>
        <p class="lede">Browsers and dashboard sessions connected to your account. Disconnecting a browser stops it syncing; its bookmarks stay where they are.</p>
      </header>

      <Show
        when={state.devices()}
        fallback={
          <div class="list" aria-busy="true">
            <For each={[0, 1, 2]}>{() => <div class="row skeleton"><span class="tile" /><div class="row-main"><span class="bar w60" /><span class="bar w30" /></div></div>}</For>
          </div>
        }
      >
        <section class="section">
          <h2 class="section-title">Browsers</h2>
          <Show
            when={browsers().length}
            fallback={
              <div class="empty small-empty">
                <p>No browsers yet. Install the bsync extension in Chrome or Firefox and sign in with this Google account.</p>
              </div>
            }
          >
            <div class="list">
              <For each={browsers()}>{(d) => <DeviceRow device={d} now={now()} onDisconnect={() => setConfirm(d)} />}</For>
            </div>
          </Show>
        </section>
        <Show when={web().length}>
          <section class="section">
            <h2 class="section-title">Dashboard sessions</h2>
            <div class="list">
              <For each={web()}>{(d) => <DeviceRow device={d} now={now()} onDisconnect={() => setConfirm(d)} />}</For>
            </div>
          </section>
        </Show>
      </Show>

      <ConfirmDialog
        open={!!confirm()}
        title={`Disconnect ${confirm()?.name ?? ''}?`}
        body={
          <p>
            {confirm()?.browser === 'web'
              ? 'That dashboard session stops syncing. If it is this one, a new session starts automatically.'
              : 'The browser stops syncing and forgets its link to your account. Its bookmarks are not deleted. Signing in again from the extension reconnects it without creating duplicates.'}
          </p>
        }
        confirm="Disconnect"
        danger
        onClose={() => setConfirm(null)}
        onConfirm={async () => {
          const d = confirm()!;
          try {
            await disconnectDeviceById(d.id);
            toast(`Disconnected ${d.name}.`, { tone: 'ok' });
          } catch (err) {
            toast(`Could not disconnect: ${String(err)}`, { tone: 'bad' });
          }
        }}
      />
    </div>
  );
}

function DeviceRow(props: { device: DeviceRecord; now: number; onDisconnect: () => void }) {
  const d = () => props.device;
  const [editing, setEditing] = createSignal(false);
  const [name, setName] = createSignal('');
  const isThis = () => d().id === currentDeviceId();
  const seen = () => (d().lastSeenAt ? Date.parse(d().lastSeenAt!) : 0);
  const active = () => isThis() || props.now - seen() < ONLINE_WINDOW;
  const extCount = () => (state.extensions() ?? []).filter((e) => e.deviceId === d().id && e.type === 'extension').length;

  const save = async () => {
    if (!editing()) return; // Enter submits, then the input's blur fires too
    const v = name().trim();
    setEditing(false);
    if (!v || v === d().name) return;
    try {
      await renameDeviceById(d().id, v);
      toast(`Renamed to ${v}.`, { tone: 'ok' });
    } catch (err) {
      toast(`Could not rename: ${String(err)}`, { tone: 'bad' });
    }
  };

  return (
    <div class="row device">
      <span class="tile plain">
        <BrowserMark browser={d().browser} size={20} />
      </span>
      <div class="row-main">
        <Show
          when={editing()}
          fallback={
            <span class="row-title static">
              {d().name}
              <Show when={isThis()}>
                <span class="tag">this dashboard</span>
              </Show>
            </span>
          }
        >
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void save();
            }}
          >
            <input class="input" value={name()} onInput={(e) => setName(e.currentTarget.value)} onBlur={save} maxLength={80} autofocus aria-label="Device name" />
          </form>
        </Show>
        <span class="row-sub">
          <span class={`dot ${active() ? 'live' : 'idle'}`} /> {active() ? 'Active' : `Last seen ${relativeTime(d().lastSeenAt, props.now)}`}
          <Show when={d().lastSyncAt}>, last change {relativeTime(d().lastSyncAt, props.now)}</Show>
          <Show when={d().browser !== 'web'}>, {extCount()} extensions</Show>
          <Show when={d().appVersion}>, v{d().appVersion}</Show>
        </span>
      </div>
      <div class="row-actions visible">
        <button
          class="icon-btn"
          onClick={() => {
            setName(d().name);
            setEditing(true);
          }}
          aria-label="Rename"
          title="Rename"
        >
          <IconEdit />
        </button>
        <button class="btn small danger-ghost" onClick={props.onDisconnect}>
          Disconnect
        </button>
      </div>
    </div>
  );
}
